import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { api } from '../api/http.js';
import { useUI } from '../ui/UIProvider.jsx';
import { Icon } from '../components/icons.jsx';
import { CountryCell, CountryFlag } from '../components/CountryFlag.jsx';

/**
 * Users page — search + filter + paginated table + side-drawer
 * detail view + row actions (ban/unban/change-plan). Talks to
 * /api/admin/users with these supported query params:
 *
 *   q=<text>       email/name/id substring match (server-side)
 *   tier=BASIC|PREMIUM
 *   country=ET|US|GB|…   ISO-3166 alpha-2
 *   status=banned|active (post-pagination filter)
 *   page=N&size=25       0-based, capped at 100 server-side
 *
 * Drawer fetches /api/admin/users/{id} for the richer detail DTO
 * (notes count, OAuth provider links, subscription source, ban
 * reason) — kept off the list payload to keep it streamable.
 *
 * @author <lukman@zshift.io>
 */
export function Users() {
  const { toast, confirm, prompt } = useUI();

  const [items,   setItems]   = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [page,    setPage]    = useState(0);
  const SIZE = 25;

  // Filter state — search input is debounced separately below.
  const [q,       setQ]       = useState('');
  const [tier,    setTier]    = useState('');
  const [status,  setStatus]  = useState('all');   // 'all'|'active'|'banned'
  const [country, setCountry] = useState('');

  // Debounce typing in the search box so we don't fire a request
  // per keystroke. 300ms feels responsive on a desk connection
  // without thrashing the API on every "a" → "ab" → "abc".
  const [qDebounced, setQDebounced] = useState('');
  useEffect(() => {
    const h = setTimeout(() => setQDebounced(q.trim()), 300);
    return () => clearTimeout(h);
  }, [q]);

  // Refetch whenever any filter changes. Reset to page 0 if a
  // filter changes (rather than the page changing) so we don't
  // ask for "page 5 of a 2-page result". The deps array
  // intentionally references all four filter sources so an
  // identity-changed handler doesn't blow up the effect.
  const reqIdRef = useRef(0);
  const fetchPage = useCallback(async (overridePage) => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(overridePage ?? page));
      params.set('size', String(SIZE));
      if (qDebounced) params.set('q', qDebounced);
      if (tier)        params.set('tier', tier);
      if (country)     params.set('country', country);
      if (status === 'banned' || status === 'active') params.set('status', status);
      const res = await api('/api/admin/users?' + params.toString());
      // Late-response guard — if a fresher request was kicked off
      // while this one was in flight, drop the stale payload.
      if (reqId !== reqIdRef.current) return;
      setItems(res?.items || []);
      setTotal(res?.total || 0);
    } catch (e) {
      console.warn(e);
      toast.error(e.message || 'Could not load users.');
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, qDebounced, tier, country, status]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  // Reset page when filters change — otherwise we'd keep page=N
  // across a filter that has < N pages and silently show "empty".
  useEffect(() => {
    if (page !== 0) setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDebounced, tier, country, status]);

  // ── Row action handlers ──────────────────────────────────────
  const banUser = async (u) => {
    const reason = await prompt({
      title: `Ban ${u.email}?`,
      message: 'The reason is written to the audit log and visible to other admins. The user loses access immediately and can be un-banned at any time.',
      placeholder: 'e.g. Repeated TOS violation after warning.',
      multiline: true,
      rows: 3,
      maxLength: 280,
      required: true,
      requiredMessage: 'Enter a reason so the audit log is useful later.',
      confirmLabel: 'Ban user',
      destructive: true,
    });
    if (reason == null) return;        // user hit Cancel / Esc
    try {
      await api(`/api/admin/users/${u.id}/ban`, { method: 'POST', body: { reason } });
      toast.success('User banned.');
      fetchPage();
    } catch (e) { toast.error(e.message || 'Ban failed.'); }
  };

  const unbanUser = async (u) => {
    const ok = await confirm({
      title: 'Un-ban this user?',
      message: `${u.email} will regain full access immediately.`,
      confirmLabel: 'Un-ban',
    });
    if (!ok) return;
    try {
      await api(`/api/admin/users/${u.id}/unban`, { method: 'POST' });
      toast.success('User un-banned.');
      fetchPage();
    } catch (e) { toast.error(e.message || 'Un-ban failed.'); }
  };

  const changePlan = async (u) => {
    const target = u.tier === 'PREMIUM' ? 'BASIC' : 'PREMIUM';
    const ok = await confirm({
      title: `Switch to ${target}?`,
      message: `${u.email} is currently on ${u.tier}. Confirm switching them to ${target}. This is admin-comped; Stripe won't be charged.`,
      confirmLabel: `Set ${target}`,
      destructive: target === 'BASIC',
    });
    if (!ok) return;
    try {
      await api(`/api/admin/users/${u.id}/plan`, { method: 'POST', body: { tier: target } });
      toast.success(`Switched to ${target}.`);
      fetchPage();
    } catch (e) { toast.error(e.message || 'Plan change failed.'); }
  };

  // ── Drawer state ────────────────────────────────────────────
  const [openUserId, setOpenUserId] = useState(null);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">User <em>management</em></h1>
          <p className="page-sub">
            Search, filter, ban, change plan. Every action is logged to the audit trail.
          </p>
        </div>
      </div>

      <div className="card">
        {/* Filter bar */}
        <div className="filterbar">
          <div className="seg">
            <SegBtn on={status === 'all'}    onClick={() => setStatus('all')}>
              All<span className="cnt">{total.toLocaleString()}</span>
            </SegBtn>
            <SegBtn on={status === 'active'} onClick={() => setStatus('active')}>Active</SegBtn>
            <SegBtn on={status === 'banned'} onClick={() => setStatus('banned')}>Banned</SegBtn>
          </div>

          <div className="fb-search">
            <Icon name="search" size={14} />
            <input
              type="text"
              placeholder="Search email, name, or ID…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          <select className="fb-select" value={tier} onChange={(e) => setTier(e.target.value)}>
            <option value="">All plans</option>
            <option value="BASIC">Basic</option>
            <option value="PREMIUM">Premium</option>
          </select>

          <input
            className="fb-select"
            style={{ width: 120 }}
            placeholder="Country (ET)"
            value={country}
            onChange={(e) => setCountry(e.target.value.toUpperCase().slice(0, 2))}
          />
        </div>

        {/* Table */}
        <div className="table-wrap">
          <table className="t">
            <thead>
              <tr>
                <th>User</th>
                <th>Country</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Joined</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>Loading…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>
                  No users match those filters.
                </td></tr>
              )}
              {!loading && items.map((u) => (
                <tr key={u.id} style={{ cursor: 'pointer' }} onClick={() => setOpenUserId(u.id)}>
                  <td>
                    <div className="user-cell">
                      <div className={`av v${(u.id % 10) + 1}`}>{initials(u.name || u.email)}</div>
                      <div>
                        <strong>{u.name || '—'}</strong>
                        <em>{u.email}</em>
                      </div>
                    </div>
                  </td>
                  <td><CountryCell code={u.country} /></td>
                  <td>
                    <span className={`pill ${u.tier === 'PREMIUM' ? 'plus' : 'free'}`}>
                      <span className="dot"/> {u.tier}
                    </span>
                  </td>
                  <td>
                    {u.banned
                      ? <span className="pill banned"><span className="dot"/> BANNED</span>
                      : <span className="pill active"><span className="dot"/> ACTIVE</span>
                    }
                  </td>
                  <td className="mono" style={{ color: 'var(--ink-3)' }}>
                    {u.createdAt ? agoString(u.createdAt) : '—'}
                  </td>
                  <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                    <button className="btn sm ghost" onClick={() => changePlan(u)} title={`Switch to ${u.tier === 'PREMIUM' ? 'Basic' : 'Premium'}`}>
                      <Icon name="plans" size={13} />
                      {u.tier === 'PREMIUM' ? 'Set Basic' : 'Set Premium'}
                    </button>
                    {u.banned
                      ? <button className="btn sm ghost" onClick={() => unbanUser(u)} title="Un-ban">
                          <Icon name="check" size={13} /> Un-ban
                        </button>
                      : <button className="btn sm danger" onClick={() => banUser(u)} title="Ban">
                          <Icon name="ban" size={13} /> Ban
                        </button>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="pager">
          <span className="pager-info">
            {loading ? '—' : `Showing ${items.length === 0 ? 0 : page * SIZE + 1}–${Math.min((page + 1) * SIZE, total)} of ${total.toLocaleString()}`}
          </span>
          <div className="pager-buttons">
            <button className="btn sm ghost" disabled={page === 0 || loading} onClick={() => setPage((p) => Math.max(0, p - 1))}>
              ← Prev
            </button>
            <button className="btn sm ghost" disabled={(page + 1) * SIZE >= total || loading} onClick={() => setPage((p) => p + 1)}>
              Next →
            </button>
          </div>
        </div>
      </div>

      {openUserId != null && (
        <UserDetailDrawer
          userId={openUserId}
          onClose={() => setOpenUserId(null)}
          onMutated={fetchPage}
          banUser={banUser}
          unbanUser={unbanUser}
          changePlan={changePlan}
        />
      )}
    </div>
  );
}

// ── Filter seg button ───────────────────────────────────────────
function SegBtn({ on, onClick, children }) {
  return <button className={on ? 'on' : ''} onClick={onClick}>{children}</button>;
}

// ── Detail drawer ───────────────────────────────────────────────
function UserDetailDrawer({ userId, onClose, onMutated, banUser, unbanUser, changePlan }) {
  const { toast } = useUI();
  const [detail, setDetail] = useState(null);
  const [usage,  setUsage]  = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Parallel: identity + usage. The usage endpoint is OPTIONAL —
    // it may not exist on older backend deploys. We pass
    // silent401:true so a missing-route 401 from a stricter security
    // chain doesn't trigger the global unauth event (which would
    // boot the operator back to /login). A genuine session-expiry
    // 401 from the identity call still works as before.
    Promise.all([
      api(`/api/admin/users/${userId}`),
      api(`/api/admin/users/${userId}/usage`, { silent401: true }).catch(() => null),
    ])
      .then(([d, u]) => { if (!cancelled) { setDetail(d); setUsage(u); setLoading(false); } })
      .catch((e) => { if (!cancelled) { toast.error(e.message || 'Could not load.'); onClose(); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const u = detail;

  return (
    <>
      <div className="drawer-overlay open" onClick={onClose} />
      <aside className="drawer open" role="dialog" aria-modal="true" aria-label="User detail">
        <div className="drawer-head">
          <h3>{loading ? 'Loading…' : (u?.name || u?.email || `User #${userId}`)}</h3>
          <button className="btn sm ghost" onClick={onClose} aria-label="Close drawer">
            <Icon name="x" size={14} />
          </button>
        </div>

        <div className="drawer-body">
          {loading && <p style={{ color: 'var(--ink-3)' }}>Loading user…</p>}
          {!loading && u && (
            <>
              <div className="drawer-section">
                <h5>Identity</h5>
                <dl className="drawer-kv">
                  <dt>Email</dt>     <dd>{u.email}</dd>
                  <dt>Name</dt>      <dd>{u.name || '—'}</dd>
                  <dt>Country</dt>   <dd><CountryCell code={u.country} /></dd>
                  <dt>User ID</dt>   <dd className="mono">{u.id}</dd>
                  <dt>Joined</dt>    <dd>{u.createdAt ? new Date(u.createdAt).toLocaleString() : '—'}</dd>
                  <dt>Last update</dt><dd>{u.updatedAt ? agoString(u.updatedAt) : '—'}</dd>
                </dl>
              </div>

              <div className="drawer-section">
                <h5>Subscription</h5>
                <dl className="drawer-kv">
                  <dt>Tier</dt>
                  <dd>
                    <span className={`pill ${u.tier === 'PREMIUM' ? 'plus' : 'free'}`}>
                      <span className="dot"/> {u.tier}
                    </span>
                  </dd>
                  <dt>Status</dt>
                  <dd>
                    {u.banned
                      ? <span className="pill banned"><span className="dot"/> BANNED</span>
                      : <span className="pill active"><span className="dot"/> ACTIVE</span>}
                  </dd>
                  {u.banned && u.banReason && (<><dt>Ban reason</dt><dd>{u.banReason}</dd></>)}
                  <dt>Source</dt>
                  <dd>{u.subscriptionSource || <span style={{color:'var(--ink-3)'}}>None / comped</span>}</dd>
                  <dt>Renews</dt>
                  <dd>{u.subscriptionUntil
                    ? new Date(u.subscriptionUntil).toLocaleDateString()
                    : <span style={{color:'var(--ink-3)'}}>Not active</span>}</dd>
                </dl>
              </div>

              <div className="drawer-section">
                <h5>Auth providers</h5>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Badge active={u.hasPassword}>Password</Badge>
                  <Badge active={u.hasGoogle}>Google</Badge>
                  <Badge active={u.hasApple}>Apple</Badge>
                </div>
              </div>

              {usage && <UsagePanel usage={usage} />}
            </>
          )}
        </div>

        {!loading && u && (
          <div className="drawer-foot">
            <button className="btn ghost" onClick={onClose}>Close</button>
            <button
              className="btn"
              onClick={() => { changePlan(u); }}
              title={`Switch to ${u.tier === 'PREMIUM' ? 'Basic' : 'Premium'}`}
            >
              <Icon name="plans" size={13} /> {u.tier === 'PREMIUM' ? 'Set Basic' : 'Set Premium'}
            </button>
            {u.banned
              ? <button className="btn primary" onClick={() => { unbanUser(u); onClose(); }}>
                  <Icon name="check" size={13} /> Un-ban
                </button>
              : <button className="btn danger" onClick={() => { banUser(u); onClose(); }}>
                  <Icon name="ban" size={13} /> Ban
                </button>
            }
          </div>
        )}
      </aside>
    </>
  );
}

// ── Usage + recent activity panel (lives inside the drawer) ─────
function UsagePanel({ usage }) {
  const daily = usage.dailyNotes || [];
  // Max value across the 7 bars; floor 1 so an all-zero week
  // doesn't divide by zero (we'd render every bar at full height
  // otherwise). A 1-min ceiling also keeps the chart visually
  // honest — bars don't shoot up dramatically for a single note.
  const max = Math.max(1, ...daily.map((d) => d.count || 0));
  const dayLabel = (iso) => {
    if (!iso) return '';
    // Returns "Mo", "Tu", "We", … from a YYYY-MM-DD string. Using
    // UTC so the bar's column matches what the backend bucketed
    // it under, regardless of the viewer's local timezone.
    const d = new Date(iso + 'T00:00:00Z');
    return ['Su','Mo','Tu','We','Th','Fr','Sa'][d.getUTCDay()];
  };

  return (
    <>
      <div className="drawer-section">
        <h5>Usage · last 7 days</h5>
        <div className="usage-bars">
          {daily.map((d, i) => {
            const heightPct = ((d.count || 0) / max) * 100;
            return (
              <div className="usage-bar-col" key={i} title={`${d.date}: ${d.count || 0} notes`}>
                <div className="usage-bar-track">
                  <div
                    className="usage-bar-fill"
                    style={{ height: `${Math.max(heightPct, d.count > 0 ? 8 : 0)}%` }}
                  />
                </div>
                <div className="usage-bar-label">{dayLabel(d.date)}</div>
              </div>
            );
          })}
        </div>
        <div className="usage-stats">
          <span className="usage-stat">
            <strong>{(usage.totalNotes || 0).toLocaleString()}</strong> notes
          </span>
          <span className="usage-stat">
            <strong>{((usage.storageBytes || 0) / 1e9).toFixed(1)}</strong> GB used
          </span>
          <span className="usage-stat">
            <strong>{(usage.chatSessions || 0).toLocaleString()}</strong> NoteChat queries
          </span>
        </div>
      </div>

      {usage.recentEvents && usage.recentEvents.length > 0 && (
        <div className="drawer-section">
          <h5>Recent activity</h5>
          <div className="usage-events">
            {usage.recentEvents.map((ev, i) => (
              <UsageEvent key={i} ev={ev} />
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function UsageEvent({ ev }) {
  // Render shape per event type. Note creations get a blue dot +
  // "Created note 'X' (kind)". Admin actions get a purple dot +
  // humanised action verb. Unknowns fall through to a generic line.
  let dotColor = 'var(--ink-4)';
  let body = null;

  if (ev.type === 'note_created') {
    dotColor = 'var(--brand-blue)';
    const title = ev.title || '(Untitled)';
    const kindLabel = ev.kind ? ` · ${ev.kind}` : '';
    body = (
      <>
        Created note <strong>{title}</strong>
        <span style={{ color: 'var(--ink-3)' }}>{kindLabel}</span>
      </>
    );
  } else if (ev.type === 'admin_action') {
    dotColor = 'var(--purple)';
    const verb = (ev.action || '').replace(/_/g, ' ');
    body = (
      <>
        <strong>{ev.actorEmail || 'system'}</strong>{' '}
        <span style={{ color: 'var(--ink-2)' }}>{verb}</span>
      </>
    );
  } else {
    body = <span>{ev.type || 'event'}</span>;
  }

  return (
    <div className="usage-event">
      <span className="usage-event-dot" style={{ background: dotColor }} />
      <div className="usage-event-body">
        {body}
        <div className="usage-event-time">{ev.at ? agoString(ev.at) : '—'}</div>
      </div>
    </div>
  );
}

function Badge({ active, children }) {
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '4px 10px',
        borderRadius: 999,
        fontSize: 12,
        background: active ? 'var(--green-soft)' : 'var(--bg-2)',
        color: active ? 'var(--green)' : 'var(--ink-3)',
        fontWeight: 600,
      }}
    >
      <span style={{
        width: 6, height: 6, borderRadius: '50%',
        background: active ? 'var(--green)' : 'var(--ink-4)',
      }}/>
      {children}
    </span>
  );
}

// ── Helpers ──────────────────────────────────────────────────────
function initials(name) {
  if (!name) return '—';
  return name.split(/\s+/).filter(Boolean).map((p) => p[0]).slice(0, 2).join('').toUpperCase();
}
function agoString(iso) {
  const ts = new Date(iso).getTime();
  if (!ts) return '—';
  const mins = Math.max(0, Math.floor((Date.now() - ts) / 60000));
  if (mins < 1) return 'now';
  if (mins < 60) return mins + 'm ago';
  const h = Math.floor(mins / 60);
  if (h < 24) return h + 'h ago';
  const d = Math.floor(h / 24);
  if (d < 30) return d + 'd ago';
  return Math.floor(d / 30) + 'mo ago';
}
